import React from "react";
import { Button, Input, theme } from "antd";
import { Select } from "../../../shared/OptionWidthControl";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { CanonicalPartSelect } from "../shared/CanonicalPartSelect";
import { canonicalPartSelectionPatch } from "../../models/shared/queryPartPayload";

// Shared part shape used by both the Create Query form (CustomerEditDrawer)
// and the Parts-tab "Add part" form (WorkflowTaskDrawer) — extracted from
// CustomerEditDrawer's original inline JSX so both places get the same
// searchable-with-add-new part name field and the same side/condition
// dropdowns instead of the Parts tab's older flat free-text fields.
// condition_preference: 1 matches the original Create Query "Add Part"
// button's default (inherited as-is, not re-derived — presumably ID 1 is
// whatever the conditionOptions list treats as its default/first entry).
export const EMPTY_PART = { uzdpart360_part_id: null, part_name: "", quantity: 1, side: null, condition_preference: 1, part_number: "", _side_manually_edited: false };

// `onAddNewPartInfo(value, idx)` is left to the caller (rather than owned
// here) because it both hits the network (POST /whatsapp/add_new_part_info)
// and updates a partsInfoOptions cache that's shared across a whole drawer
// session, not scoped to one PartsEditor instance.
export function PartsEditor({
  parts, setParts,
  sideOptions = [], conditionOptions = [],
  onAddNewPartInfo,
  queryPk,
  label = "Parts",
  renderPhotos,
}) {
  const { token } = theme.useToken();
  const updatePart = (idx, fields) => setParts(prev => {
    const next = [...prev];
    next[idx] = { ...next[idx], ...fields };
    return next;
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{label}</div>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setParts(prev => [...prev, { ...EMPTY_PART }])}
        >
          Add Part
        </Button>
      </div>
      {parts.length === 0 ? (
        <div style={{ fontSize: 12, color: token.colorTextSecondary, textAlign: "center", padding: "6px 0" }}>
          No parts added yet
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {parts.map((part, idx) => (
            <div key={part.id ?? part.client_key ?? idx} style={{
              border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 6,
              padding: "8px 10px", background: token.colorBgContainer,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {/* Row 1: name + qty + delete */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <CanonicalPartSelect
                  style={{ flex: 1 }}
                  value={part.uzdpart360_part_id ?? null}
                  legacyLabel={part.uzdpart360_part_id == null ? part.part_name : null}
                  onAddLegacy={onAddNewPartInfo ? value => onAddNewPartInfo(value, idx) : null}
                  queryPk={queryPk}
                  queryPartId={part.id}
                  onChange={(id, option, alias) => updatePart(idx, canonicalPartSelectionPatch(part, id, option, alias))}
                />
                <Input
                  type="number"
                  min={1}
                  value={part.quantity}
                  onChange={e => updatePart(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                  style={{ width: 56 }}
                  size="small"
                  prefix={<span style={{ fontSize: 10, color: token.colorTextSecondary }}>×</span>}
                />
                <Button
                  type="text" danger size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => setParts(prev => prev.filter((_, i) => i !== idx))}
                />
              </div>
              {/* Side is capped at 240px; Condition fits its widest label. */}
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 240px) auto", gap: 6, justifyContent: "start" }}>
                <Select
                  size="small"
                  placeholder="Side"
                  style={{ width: "100%", minWidth: 0 }}
                  allowClear
                  value={part.side ?? null}
                  onChange={val => updatePart(idx, { side: val ?? null, _side_manually_edited: true, _side_auto_suggested: false })}
                  options={sideOptions.filter(option => !option.disabled || String(option.value) === String(part.side))}
                />
                <div style={{ display: "grid", minWidth: 0 }}>
                  <div aria-hidden="true" style={{ gridArea: "1 / 1", visibility: "hidden", height: 0, overflow: "hidden", display: "grid", fontSize: 14, paddingInline: 22, pointerEvents: "none" }}>
                    {["Condition", ...conditionOptions.map(option => option.label)].map((label, index) => (
                      <span key={index} style={{ gridArea: "1 / 1", whiteSpace: "nowrap" }}>{label}</span>
                    ))}
                  </div>
                <Select
                  size="small"
                  placeholder="Condition"
                  style={{ gridArea: "1 / 1", width: "100%", minWidth: 0 }}
                  allowClear
                  value={part.condition_preference ?? null}
                  onChange={val => updatePart(idx, { condition_preference: val ?? null })}
                  options={conditionOptions.filter(option => !option.disabled || String(option.value) === String(part.condition_preference))}
                />
                </div>
              </div>
              {/* Row 3: full-width part number */}
              <Input
                size="small"
                placeholder="Part #"
                value={part.part_number}
                onChange={e => updatePart(idx, { part_number: e.target.value })}
              />
              {renderPhotos?.(part)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
